import { useState } from "react";
import { Link } from "react-router-dom";
import { PlusIcon } from "lucide-react";

import { useSites } from "@/api/sites";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExportCsvButton } from "@/components/ExportCsvButton";
import { Card, CardContent } from "@/components/ui/card";
import { useAuthStore } from "@/store/auth";
import { AddSiteDialog } from "./AddSiteDialog";

function healthVariant(pct: number): "online" | "warning" | "error" {
  if (pct > 80) return "online";
  if (pct >= 50) return "warning";
  return "error";
}

export function SitesListPage() {
  const { data, isLoading, isError } = useSites();
  const [addOpen, setAddOpen] = useState(false);
  const role = useAuthStore((s) => s.user?.role);
  const canManage = role === "admin";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Sites</h1>
          <p className="text-sm text-muted-foreground">
            {data ? `${data.total} active` : "Loading…"}
          </p>
        </div>
        <div className="flex gap-2">
          {/* T4c — same mechanism as every other list. */}
          <ExportCsvButton
            rows={data?.items ?? []}
            filename="sites"
            columns={[
              { header: "Name", value: (r) => r.name },
              { header: "Code", value: (r) => r.code ?? "" },
              { header: "City", value: (r) => r.city ?? "" },
              { header: "State", value: (r) => r.state ?? "" },
              { header: "NVR host", value: (r) => r.nvr_host ?? "" },
              { header: "NVR configured", value: (r) => (r.nvr_configured ? "yes" : "no") },
              { header: "Active", value: (r) => (r.is_active ? "yes" : "no") },
            ]}
          />
          {canManage && (
            <Button onClick={() => setAddOpen(true)}>
              <PlusIcon className="mr-2 h-4 w-4" />
              Add site
            </Button>
          )}
        </div>
      </div>

      {isLoading && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Loading sites…
          </CardContent>
        </Card>
      )}
      {isError && (
        <Card className="border-destructive/40">
          <CardContent className="py-10 text-center text-sm text-destructive">
            Could not load sites.
          </CardContent>
        </Card>
      )}

      {data && data.items.length === 0 && (
        <Card>
          <CardContent className="space-y-2 py-10 text-center">
            <p className="text-sm text-muted-foreground">No sites yet.</p>
            {canManage && (
              <p className="text-xs text-muted-foreground">
                Use <strong>Add site</strong> to register your first branch.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {data && data.items.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Code</th>
                  <th className="px-4 py-3 text-left">Location</th>
                  <th className="px-4 py-3 text-right">Cameras</th>
                  <th className="px-4 py-3 text-right">Health</th>
                  <th className="px-4 py-3 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((s) => {
                  const c = s.camera_counts;
                  const pct = s.health_pct ?? 100;
                  return (
                    <tr key={s.id} className="border-b last:border-0">
                      <td className="px-4 py-3">
                        <Link
                          to={`/sites/${s.id}`}
                          className="font-medium hover:underline"
                        >
                          {s.name}
                        </Link>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">
                        {s.code ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {[s.city, s.state, s.country]
                          .filter(Boolean)
                          .join(", ") || "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {c ? `${c.online}/${c.total}` : "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Badge variant={healthVariant(pct)}>
                          {Math.round(pct)}%
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={s.is_active ? "online" : "offline"}>
                          {s.is_active ? "active" : "inactive"}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {canManage && (
        <AddSiteDialog open={addOpen} onOpenChange={setAddOpen} />
      )}
    </div>
  );
}
